import map, {TRACKING_SERVICE, PARAM_DELIMITER, DATA_DELIMITER} from './options.js';

//region Initialize
export default function initAnalytics() {
	initContact();
	initClick();
	initScroll();
	initPDF();
	initSubmittedData();
};

const initSubmittedData = () => {
	const submittedData = document.querySelector('.js-submitted-form-data');
	if (!submittedData) return;
	const data = submittedData.dataset;
	console.log('Submitted form data', data);
};

// Default contact events (tel, mailto) will be overridden,
// if another data-ga-* attribute is present on the element.
const initContact = () => {
	const contactCategory = map.getCategory('contact');
	for (let type of contactCategory.getActionTypes()) {
		addClickTrackers('a[href^="' + type + '"]', (element) => {
			if (ignoreDefaultContact(element)) return;
			const title = contactCategory.getTitle();
			const value = element.href.replace(type + ':', '');
			const action = contactCategory.getAction(type);
			send(title, action, value);
		});
	}
};

const initClick = () => {
	addClickTrackers('[data-ga-click]', (element) => {
		const data = separateData(element.dataset.gaClick);
		data.forEach(track);
	});
};

const initScroll = () => {
	addScrollTrackers('[data-ga-scroll]', (element) => {
		const data = separateData(element.dataset.gaScroll);
		data.forEach(track);
	});
};

const initPDF = () => {
	const linkCategory = map.getCategory('link');
	const pdfAction = linkCategory.getAction('pdf');
	if (!pdfAction) return;
	addClickTrackers('a[href$=".pdf"]', (element) => {
		const title = linkCategory.getTitle();
		send(title, pdfAction, element.href);
	});
};
//endregion

//region Trackers
const addClickTrackers = (selector, handler) => {
	const nodes = query(selector);
	if (!nodes) return;

	nodes.forEach((element) => {
		const tracker = () => {
			handler(element);
			element.removeEventListener('click', tracker);
		};
		element.addEventListener('click', tracker)
	});
};

const addScrollTrackers = (selector, handler) => {
	const nodes = query(selector);
	if (!nodes) return;

	nodes.forEach((element) => {
		const tracker = () => {
			if (inView(element)) {
				handler(element);
				document.removeEventListener('scroll', tracker);
			}
		};
		document.addEventListener('scroll', tracker);
	});
};
// endregion

// region Helpers
const query = (selector) => {
	const nodes = document.querySelectorAll(selector);
	// Empty NodeLists obviously have no elements to track.
	if (nodes && nodes.length) return nodes;
};

const inView = (element) => {
	const rect = element.getBoundingClientRect();
	return rect.top >= 0 && rect.left >= 0 &&
		rect.bottom <= window.innerHeight &&
		rect.right <= window.innerWidth;
};

const ignoreDefaultContact = (element) => {
	const data = element.dataset;
	return 'gaClick' in data || 'gaScroll' in data;
};
// endregion

// region Event model
/**
 * Split data-attribute contents by a double ampersand delimiter,
 * reducing parameter string into individual events.
 * Uses this format: data-ga-event="<tracking>&&<tracking>&&<tracking>"
 *
 * @param {string} data - String of double-ampersand-separated tracking strings.
 * @returns {Array} - Array of tracking strings.
 */
const separateData = (data) => data.split(DATA_DELIMITER);

/**
 * Split data-attribute contents by pipe delimiter,
 * in order to parse tracking event parameters.
 * Uses this format: data-ga-event="<param>|<param>|<param>"
 *
 * @param {string} params - String of pipe-separated tracking parameters.
 * @returns {Array} - Array of tracking parameters.
 */
const separateParams = (params) => params.split(PARAM_DELIMITER);

/**
 * Parse and send tracking event data using this format: category|action|label
 * May separate multiple tracking data by a double ampersand delimiter.
 *
 * @param {string} tracking - Tracking data separated by a double ampersand delimiter.
 */
const track = (tracking) => {
	// Split tracking string into parameters.
	const params = separateParams(tracking);

	// Determine category and convert to label strings.
	const categoryName = params[0];
	const actionName = params[1];
	const value = params[2] || '';

	// Determine title and action based on category declaration.
	const category = map.getCategory(categoryName);
	const title = category ? category.getTitle() : categoryName;
	const action = category ? (category.getAction(actionName) || actionName) : actionName;

	send(title, action, value);
};

/**
 * Parse the available (and desired) tracking service from the window object.
 *
 * @returns {String} - String representing the available tracking service, if any.
 */
const getTrackingService = () => TRACKING_SERVICE in window ? TRACKING_SERVICE : '';

/**
 * Send a tracking event.
 * The timeout ensures that the tracking service loads asynchronously before firing.
 * Log to console instead if tracking service object is not available.
 *
 * @param {string} category - eventCategory
 * @param {string} action - eventAction
 * @param {string} label - eventLabel
 */
const send = (category, action, label) => setTimeout(() => {
	const has_gtag = 'gtag' in window;
	const has_ga = 'ga' in window;

	if (!(has_gtag || has_ga)) {
		const service = getTrackingService();
		console.log('No tracking service available to send event ("' + service + '" was declared):'
			+ '\n* Category =', category
			+ '\n* Action =', action
			+ '\n* Label =', label);
		return;
	}

	if (has_gtag) {
		gtag('event', action, {event_category: category, event_label: label});
	}
	if (has_ga) {
		ga('send', 'event', category, action, label);
	}
}, 1000);
// endregion